import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  HUD,
  SettingsPanel,
  HUDBookmarks,
  useFeature,
  HUDNavDirection,
  HUDDirection,
  HUDMainItemMode,
  useViewportSize,
} from '@ole-ui/ole-ui-components';
import { Button } from '@patternfly/react-core';
import { Link } from 'react-mobx-router5';
import { useTranslation } from 'react-i18next';
import useMobxStores from 'hooks/useMobxStores';
import { observer } from 'mobx-react';
import SVG from 'react-inlinesvg';
import { withErrorBoundary } from 'react-error-boundary';
import ComponentError from 'components/Error/ComponentError';
import { handleError } from 'services/ErrorService';

import AiAssistIconFilled from 'img/icons/ai-assist-icon-filled.svg';
import AiAssistIcon from 'img/icons/ai-assist-icon.svg';

import BookmarksIconFilled from 'img/icons/bookmarks-icon-filled.svg';
import BookmarksIcon from 'img/icons/bookmarks-icon.svg';

import VideoIconDisabled from 'img/icons/tv-show-icon-disabled.svg';
import VideoIconFilled from 'img/icons/tv-show-icon-filled.svg';

import FeedbackIcon from 'img/icons/feedback-icon.svg';
import FeedbackIconFilled from 'img/icons/feedback-icon-filled.svg';

import BookshelfIcon from 'img/icons/bookshelf-icon.svg';

import SettingsIconFilled from 'img/icons/settings-icon-filled.svg';
import SettingsIcon from 'img/icons/settings-icon.svg';

import { EnablementFeatureFlags } from 'config/featureFlags';
import { aiAssistantContext } from 'components/Course/AiAssist/store';
import CourseToC from './CourseToC';
import useAutoScrollToPageVideo from './hooks/useAutoScrollToPageVideo';
import CourseStore from './store';
import CourseHUDPortals from './CourseHUDPortals';

// When the HUD bar is narrower than this threshold (px) the dock icons are
// replaced with a single "..." overflow button that reveals them in a panel.
// Value is set slightly above the dock bar's measured natural content width
// (~797px: all nav items + dock icons) so overflow triggers before any icon
// gets clipped by overflow-x:hidden. The extra margin also accounts for the
// 16px drag-handle that sits at the panel's left edge.
const HUD_OVERFLOW_THRESHOLD = 820;

// Internal eventKey used by the HUD library for the mobile hamburger panel.
const MOBILE_MENU_UNIQUE_ID = '__mobile-items__';

type Props = {
  courseStore: CourseStore;
  isHoveringVideoPlayer: boolean;
  isVideoPlayerEnabled: boolean;
  onDirectionChange: (direction: HUDDirection) => void;
  onOverviewButtonClicked: () => void;
  isOverviewDisabled: boolean;
  onVideoPlayerChange: (isEnabled: boolean) => void;
  sideToCRef?: React.RefObject<HTMLDivElement>;
  isSideToCActive?: boolean;
  setSideToCActive?: (isActive: boolean) => void;
  sidebarPosition?: 'left' | 'right';
  setSidebarPosition?: (position: 'left' | 'right') => void;
  aiAssistRef?: React.RefObject<HTMLDivElement>;
  isAiQuickMenuEnabled?: boolean;
  setIsAiQuickMenuEnabled?: (isEnabled: boolean) => void;
  /**
   * Notifies the host view when a HUD panel becomes active/inactive.
   * Used to suppress floating overlays (like the AI quick menu) while any HUD sidebar/panel is open.
   */
  onActiveHUDPanelChange?: (activeItem: string | null) => void;
  /**
   * Notifies the host view when the Table of Contents display mode changes
   * (Sidebar / Full view / Minimized).
   */
  onTocModeChange?: (mode: HUDMainItemMode) => void;
};

const TocModeReporter: React.FC<{
  mode: HUDMainItemMode;
  onChange?: (mode: HUDMainItemMode) => void;
}> = ({ mode, onChange }) => {
  useEffect(() => {
    onChange?.(mode);
  }, [mode, onChange]);

  return null;
};

const CourseHUD = (props: Props) => {
  const {
    courseStore,
    isHoveringVideoPlayer,
    isVideoPlayerEnabled,
    onDirectionChange,
    onOverviewButtonClicked,
    isOverviewDisabled,
    onVideoPlayerChange,
    sideToCRef,
    isSideToCActive,
    setSideToCActive,
    sidebarPosition,
    setSidebarPosition,
    aiAssistRef,
    isAiQuickMenuEnabled,
    setIsAiQuickMenuEnabled,
    onActiveHUDPanelChange,
    onTocModeChange,
  } = props;
  const { t, i18n } = useTranslation();
  const isEnglishPage = i18n.language?.startsWith('en') ?? true;
  const { eventStore, routerStore, userStore } = useMobxStores();
  const { route } = routerStore;

  const aiDisclaimerAccepted = Boolean(
    userStore.userSettings?.ai_assistant_disclaimer_accepted,
  );
  const [activeHUDPanel, setActiveHUDPanel] = useState<string | null>(null);
  // Track the current ToC display mode from the HUD context so we can
  // avoid triggering the "external ToC" sidebar state machine when the
  // ToC is rendered as a HUD panel (Full view / Minimized).
  const tocModeRef = React.useRef<HUDMainItemMode>(HUDMainItemMode.FULL);

  // Ensure the host view doesn't keep stale "panel open" state if the HUD unmounts.
  useEffect(
    () => () => {
      onActiveHUDPanelChange?.(null);
    },
    [onActiveHUDPanelChange],
  );

  // Keep "mobile" aligned with the HUD breakpoint (phones + tablets).
  const { isLowerThanLg: isMobileMode } = useViewportSize();

  const isBookshelfEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.BookshelfEnabled,
    defaultFlagValue: false,
  });

  const isAiAssistEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.AIAssistantEnabled,
    defaultFlagValue: false,
  });

  const aiAssistantStore = useContext(aiAssistantContext);
  const isAiAssistSidebarOpen = Boolean(
    isAiAssistEnabled && aiAssistantStore?.isOpen,
  );

  const selectedText = (aiAssistantStore?.selectedText ?? '').trim();

  // Ref on the HUD root so we can observe when the HUD bar becomes narrow.
  const hudWrapperRef = useRef<HTMLDivElement>(null);
  const [isHUDNarrow, setIsHUDNarrow] = useState(false);

  useEffect(() => {
    const el = hudWrapperRef.current;
    if (!el) return undefined;
    const resizeObserver = new ResizeObserver(([entry]) => {
      setIsHUDNarrow(entry.contentRect.width < HUD_OVERFLOW_THRESHOLD);
    });
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, []);

  // When the AI sidebar opens while the HUD mobile / overflow menu is showing,
  // close that menu so the two panels don't fight for the same screen area.
  // Using the functional updater lets us read the current panel without adding
  // it to the deps, so this only fires on AI open/close — not when the user
  // explicitly taps "..." while AI is already active.
  useEffect(() => {
    if (!isAiAssistSidebarOpen) return;
    setActiveHUDPanel((current) => {
      if (
        current === MOBILE_MENU_UNIQUE_ID ||
        current === 'hud-overflow-menu'
      ) {
        return null;
      }
      return current;
    });
  }, [isAiAssistSidebarOpen]);

  let navCustomContent: React.ReactNode | undefined;

  if (isMobileMode && isAiAssistEnabled && aiAssistantStore) {
    if (isAiAssistSidebarOpen) {
      navCustomContent = (
        <Button
          aria-label={t('Close AI Assistant')}
          className="hud-course-view__ai-assist-mobile-hud-btn"
          data-analytics-id="hud-ai-assist-close-ole-lp"
          isBlock
          onClick={() => {
            aiAssistantStore.setIsOpen(false);
          }}
          variant="primary"
        >
          {t('Close AI Assistant')}
        </Button>
      );
    } else if (selectedText) {
      navCustomContent = (
        <Button
          aria-label={t('Get recommendations')}
          className="hud-course-view__ai-assist-mobile-hud-btn"
          data-analytics-id="hud-ai-assist-get-recommendations-ole-lp"
          isBlock
          onClick={() => {
            if (!aiDisclaimerAccepted) {
              aiAssistantStore.setPendingRecommendationText(selectedText);
              aiAssistantStore.setIsOpen(true);
            } else {
              aiAssistantStore.requestRecommendations(selectedText);
            }
          }}
          variant="primary"
        >
          {t('Get recommendations')}
        </Button>
      );
    }
  }

  useAutoScrollToPageVideo(isVideoPlayerEnabled);

  const {
    bookmarkList,
    canShowBookshelf,
    canShowVideoPlayer,
    courseCode,
    courseSlug,
    courseTotalProgress,
    courseVersion,
    currentPageNumber,
    isFreeToolbar,
    isLatestVersion,
    isLoadingInitialData,
    isLesson,
    page,
    pageSlug,
    sortedVersionDesc,
    totalPageNumber,
    canShowOverview,
  } = courseStore;

  const handleOverviewClicked = () => {
    if (canShowOverview) {
      // redirect to overview page
      routerStore.navigate('courses:overview', {
        course: courseSlug,
      });
    } else {
      onOverviewButtonClicked();
    }
  };

  const bookmarks = bookmarkList.map((bookmark) => ({
    name: bookmark.title,
    id: bookmark.section,
  }));

  const isFeedbackDrawerOpen =
    eventStore.isDrawerActive === 'feedback' && eventStore.isDrawerOpen;

  return (
    <HUD
      ref={hudWrapperRef}
      activeItem={activeHUDPanel}
      className={`hud-course-view__hud hud-course-view__hud-overflow-wrapper pf-v5-theme-dark${
        isHUDNarrow ? ' hud-course-view__hud-overflow-wrapper--narrow' : ''
      }`}
      isMouseMovementDisabled={isHoveringVideoPlayer}
      itemMaxHeightOffset={0}
      key="course-hud"
      localStorageKeyPrefix="course-hud"
      onActiveItemChange={(activeItem: string | null) => {
        setActiveHUDPanel(activeItem);
        onActiveHUDPanelChange?.(activeItem);
      }}
      onDirectionChange={onDirectionChange}
      portals={
        <CourseHUDPortals
          courseStore={courseStore}
          isOverviewDisabled={isOverviewDisabled}
          onOverviewButtonClicked={handleOverviewClicked}
          sideToCRef={sideToCRef}
          isSideToCActive={isSideToCActive}
          setSideToCActive={setSideToCActive}
          aiAssistRef={aiAssistRef}
          aiAssistantStore={aiAssistantStore}
          isAiAssistSidebarOpen={isAiAssistSidebarOpen}
          tocModeRef={tocModeRef}
          sidebarPosition={sidebarPosition}
        />
      }
      position={isMobileMode ? 'fixed' : 'sticky'}
      progress={courseTotalProgress}
      progressBarProps={{
        'aria-label': t('Course progress'),
      }}
    >
      <HUD.MainItem
        buttonProps={{
          'aria-label': t('Toggle Table of Contents panel'),
          'data-analytics-id': `hud-toc-ole-lp`,
          disabled: isLoadingInitialData,
        }}
        hasPadding
        isResizable
        label={page?.title || ''}
        minHeight={200}
        isExternalPanelActive={isSideToCActive}
        onToggle={() => {
          if (
            setSideToCActive &&
            tocModeRef.current === HUDMainItemMode.EXTERNAL &&
            !isMobileMode
          ) {
            setSideToCActive(!isSideToCActive);
          }
        }}
      >
        {(contextValues) => (
          <>
            <TocModeReporter
              mode={contextValues.mainItemMode}
              onChange={onTocModeChange}
            />
            <CourseToC
              courseStore={courseStore}
              isOverviewDisabled={isOverviewDisabled}
              onOverviewButtonClicked={handleOverviewClicked}
              hudDirection={contextValues.direction}
              mode={contextValues.mainItemMode}
              setActiveHUDPanel={setActiveHUDPanel}
            />
          </>
        )}
      </HUD.MainItem>
      <HUD.Navigation
        currentPage={currentPageNumber}
        customContent={navCustomContent}
        nextButtonProps={{
          'aria-label': t('Go to next page'),
          'data-analytics-id': `top-next-btn-ole-lp`,
          disabled: courseStore.isNextButtonDisabled,
        }}
        onNavigate={(hudNavDirection) => {
          if (hudNavDirection === HUDNavDirection.NEXT) {
            courseStore.goToNextPage();
          } else if (hudNavDirection === HUDNavDirection.PREV) {
            courseStore.goToPrevPage();
          }
        }}
        prevButtonProps={{
          'aria-label': t('Go to previous page'),
          'data-analytics-id': `top-previous-btn-ole-lp`,
          disabled: courseStore.isPrevButtonDisabled,
        }}
        totalPages={totalPageNumber}
      />
      <HUD.Items key="items">
        <HUD.Item
          activeIcon={<SVG src={BookmarksIconFilled} />}
          buttonProps={{
            'aria-label': t('Toggle bookmarks panel'),
            'data-analytics-id': `hud-bookmarks-ole-lp`,
            disabled: isLoadingInitialData,
          }}
          eventKey="bookmarks"
          hasPadding
          icon={<SVG src={BookmarksIcon} />}
          title={t('Bookmarks')}
          type="panel"
        >
          <HUDBookmarks
            bookmarks={bookmarks}
            isCurrentPageBookmarked={courseStore.isCurrentPageBookmarked}
            onBookmarkChange={() => {
              courseStore.handleCurrentPageAsBookmarked();
            }}
            onBookmarkRemove={(id: string) => {
              courseStore.handleBookmark(id, undefined);
            }}
            renderBookmarkLink={({ id, name }) => {
              if (id === pageSlug || !id) {
                return <p key={name}>{name}</p>;
              }
              return (
                <Link
                  data-analytics-id="hud-bookmarks-add-item-ole-lp"
                  href
                  key={id}
                  routeName="courses:page"
                  routeParams={{
                    course: (route as any)?.params?.course,
                    page: id,
                  }}
                >
                  {name}
                </Link>
              );
            }}
          />
        </HUD.Item>

        {isAiAssistEnabled &&
          aiAssistantStore &&
          (isEnglishPage ? (
            <HUD.Item
              buttonProps={{
                'aria-label': t('Toggle Learning Assistant sidebar'),
                'data-analytics-id': 'hud-ai-assist-ole-lp',
                disabled: isLoadingInitialData,
              }}
              eventKey="ai-assist"
              activeIcon={<SVG src={AiAssistIconFilled} />}
              icon={
                <SVG
                  src={
                    isAiAssistSidebarOpen ? AiAssistIconFilled : AiAssistIcon
                  }
                />
              }
              isEnabled={!!isAiAssistSidebarOpen}
              onChange={(isEnabled) => {
                aiAssistantStore.setIsOpen(isEnabled);
              }}
              title={t('Learning Assistant')}
              type="toggle"
            />
          ) : (
            <HUD.Item
              eventKey="ai-assist"
              activeIcon={<SVG src={AiAssistIconFilled} />}
              icon={<SVG src={AiAssistIcon} />}
              title={t(
                'The AI assistant is currently available only on English pages. Support for other languages is coming soon.',
              )}
              type="toggle"
            />
          ))}
        {canShowVideoPlayer && (
          <HUD.Item
            buttonProps={{
              'aria-label': 'Toggle course video player',
              'data-analytics-id': `hud-video-toggle-ole-lp`,
              disabled: isLoadingInitialData,
            }}
            eventKey="video-player"
            icon={
              <SVG
                src={isVideoPlayerEnabled ? VideoIconFilled : VideoIconDisabled}
              />
            }
            isEnabled={isVideoPlayerEnabled}
            onChange={onVideoPlayerChange}
            title={
              isVideoPlayerEnabled
                ? t('Disable video player')
                : t('Enable video player')
            }
            type="toggle"
          />
        )}
        {!isFreeToolbar && (
          <HUD.Item
            buttonProps={{
              'aria-label': 'Send feedback',
              'data-analytics-id': `hud-feedback-ole-lp`,
              disabled: isLoadingInitialData,
              role: 'link',
            }}
            eventKey="feedback"
            icon={
              <SVG
                src={isFeedbackDrawerOpen ? FeedbackIconFilled : FeedbackIcon}
              />
            }
            isEnabled={isFeedbackDrawerOpen}
            onChange={() => {
              courseStore.openFeedbackDrawer();
            }}
            title={t('Feedback')}
            type="toggle"
          />
        )}
        {isBookshelfEnabled && canShowBookshelf && (
          <HUD.Item
            buttonProps={{
              'aria-label': 'Access course Bookshelf',
              'data-analytics-id': `hud-bookshelf-ole-lp`,
              disabled: isLoadingInitialData,
              role: 'link',
            }}
            eventKey="bookshelf"
            icon={<SVG src={BookshelfIcon} />}
            onChange={() => {
              window.open(courseStore.bookshelfURL, '_blank');
            }}
            title={t('Access Bookshelf')}
            type="custom"
          />
        )}
        <HUD.Item
          activeIcon={<SVG src={SettingsIconFilled} />}
          buttonProps={{
            'aria-label': t('Toggle settings panel'),
            'data-analytics-id': `hud-settings-ole-lp`,
          }}
          eventKey="settings"
          hasPadding
          icon={<SVG src={SettingsIcon} />}
          notificationBadge={
            isLoadingInitialData ? undefined : !isLatestVersion
          }
          title={t('Settings')}
          type="panel"
        >
          {(contextValues) => (
            <div style={{ width: '100%', height: '100%' }}>
              <SettingsPanel
                code={courseCode}
                currentBehavior={contextValues.behavior}
                currentDirection={contextValues.direction}
                currentMainItemMode={contextValues.mainItemMode}
                currentVersion={courseVersion}
                currentOpacity={contextValues.opacity}
                currentSidebarPosition={sidebarPosition}
                isAiAssistEnabled={isAiAssistEnabled}
                isAiQuickMenuEnabled={isAiQuickMenuEnabled}
                isLatestVersion={isLatestVersion}
                isVideoPlayerEnabled={isVideoPlayerEnabled}
                isLesson={isLesson}
                onBehaviorChange={(behavior) =>
                  contextValues.setBehavior(behavior)
                }
                onChangeRoute={({ routeName, routeParams }) =>
                  routerStore.navigate(routeName, routeParams)
                }
                onDirectionChange={(hudDirection) =>
                  contextValues.setDirection(hudDirection)
                }
                onMainItemModeChange={(mode) =>
                  contextValues.setMainItemMode(mode)
                }
                onOpacityChange={(opacity) => contextValues.setOpacity(opacity)}
                onSidebarPositionChange={setSidebarPosition}
                onAiQuickMenuEnabledChange={setIsAiQuickMenuEnabled}
                onVideoPlayerChange={onVideoPlayerChange}
                versions={sortedVersionDesc}
              />
            </div>
          )}
        </HUD.Item>
      </HUD.Items>
    </HUD>
  );
};

export default withErrorBoundary(
  observer(CourseHUD),
  ComponentError,
  handleError,
);
